import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Building2, Eye, Loader2, LogIn, RefreshCw } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;
const PORTAL_HOSTS = new Set(["www.casadiriposoperte.it", "casadiriposoperte.it", "localhost", "127.0.0.1"]);
const FILTERS = [
  { id: "pending", label: "In attesa" },
  { id: "trial", label: "In prova" },
  { id: "expired", label: "Scadute" },
  { id: "tutte", label: "Tutte" },
];

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT");
  } catch {
    return iso;
  }
}

function statusLabel(item) {
  if (item.approval_status === "pending") return "In attesa";
  if (item.approval_status === "rejected") return "Rifiutata";
  if (item.subscription_status === "trial") return "In prova";
  if (item.subscription_status === "active") return "Abbonamento attivo";
  if (item.subscription_status === "expired") return "Scaduta";
  return item.subscription_status || item.approval_status || "—";
}

function trialText(item) {
  if (!item) return "";
  const inTrial = item.subscription_status === "trial";
  const expiredUnpaid = item.subscription_status === "expired" && item.trial_days_remaining === 0 && !item.pagamento_effettuato;
  if (!inTrial && !expiredUnpaid) return "";
  const days = item.trial_days_remaining;
  if (days == null) return "In prova";
  if (days <= 0) return "Prova scaduta";
  const left = days === 1 ? "1 giorno rimasto" : `${days} giorni rimasti`;
  return item.trial_end ? `${left} · fino al ${formatWhen(item.trial_end)}` : left;
}

function paymentBadgeClass(stato) {
  if (stato === "paid") return "border-emerald-200 bg-emerald-50 text-emerald-800";
  if (stato === "pending" || stato === "processing") return "border-amber-200 bg-amber-50 text-amber-800";
  if (stato === "nessuno") return "border-slate-200 bg-slate-50 text-slate-600";
  return "border-rose-200 bg-rose-50 text-rose-800";
}

function trialDialogText(item) {
  const live = trialText(item);
  if (live) return live;
  if (item?.subscription_status === "active" || item?.pagamento_effettuato || item?.trial_end) return "Prova conclusa";
  return "Prova non avviata";
}

function paymentSummary(item) {
  const label = item.pagamento_stato_label || "Nessun pagamento";
  if (item.pagamento_effettuato && item.subscription_plan_label) {
    return `Abbonamento: ${label} · ${item.subscription_plan_label}`;
  }
  return `Abbonamento: ${label}`;
}

const SCHEDA_GROUPS = [
  {
    title: "Struttura",
    fields: [
      ["nome_struttura", "Nome"],
      ["tipo_struttura", "Tipo"],
      ["email_struttura", "Email"],
      ["sito_web", "Sito web"],
      ["facebook", "Facebook"],
    ],
  },
  {
    title: "Indirizzo",
    fields: [
      ["via", "Via"],
      ["numero_civico", "Civico"],
      ["comune", "Comune"],
      ["provincia", "Provincia"],
    ],
  },
  {
    title: "Referente",
    fields: [
      ["nome_referente", "Nome"],
      ["telefono_referente", "Telefono"],
    ],
  },
  {
    title: "Capacità e rette",
    fields: [
      ["posti_totali", "Posti totali"],
      ["posti_diurni", "Posti diurni"],
      ["prezzo_singola_wc", "Singola con bagno", "euro"],
      ["prezzo_doppia_wc", "Doppia con bagno", "euro"],
      ["prezzo_singola_no_wc", "Singola senza bagno", "euro"],
      ["prezzo_doppia_no_wc", "Doppia senza bagno", "euro"],
    ],
  },
  {
    title: "Servizi",
    fields: [
      ["presenza_del_medico", "Presenza del medico", "bool"],
      ["infermieri", "Infermieri", "bool"],
      ["giardino_spazi_esterni_fruibili", "Giardino e spazi esterni", "bool"],
      ["attivita_occupazionali", "Attività occupazionali", "bool"],
      ["note_attivita", "Note attività"],
    ],
  },
];

const euroFormat = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });

function fieldFilled(value, kind) {
  if (kind === "bool") return value === true;
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "number") return !Number.isNaN(value);
  return Boolean(value);
}

function formatField(value, kind) {
  if (!fieldFilled(value, kind)) return kind === "bool" ? "Non indicato" : "Non compilato";
  if (kind === "bool") return "Sì";
  if (kind === "euro") return euroFormat.format(Number(value));
  return String(value);
}

function schedaStats(scheda) {
  const fields = SCHEDA_GROUPS.flatMap((group) => group.fields);
  const filled = fields.filter(([key, , kind]) => fieldFilled(scheda?.[key], kind)).length;
  const images = Number(scheda?.immagini) || 0;
  const total = fields.length + 1;
  return { filled: filled + (images > 0 ? 1 : 0), total, images };
}

export default function RegistrazioniPortaleAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [stato, setStato] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (nextStato = stato) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/api/admin/casadiriposoperte/registrazioni?stato=${encodeURIComponent(nextStato)}`,
        { headers }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore caricamento registrazioni");
      setItems(json.registrazioni || []);
    } catch (error) {
      toast.error(error.message || "Errore registrazioni");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [stato, headers]);

  useEffect(() => {
    load(stato);
  }, [stato]); // eslint-disable-line react-hooks/exhaustive-deps

  async function act(id, action) {
    setBusyId(id);
    try {
      const res = await fetch(`${API}/api/admin/casadiriposoperte/registrazioni/${id}/${action}`, {
        method: "POST",
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Operazione non riuscita");
      toast.success(json.message || "Aggiornata");
      await load(stato);
    } catch (error) {
      toast.error(error.message || "Operazione non riuscita");
    } finally {
      setBusyId("");
    }
  }

  async function enterPortal(item) {
    const popup = window.open("", "_blank");
    setBusyId(item.id);
    try {
      const res = await fetch(`${API}/api/admin/casadiriposoperte/registrazioni/${item.id}/entra`, {
        method: "POST",
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Accesso al portale non riuscito");
      const url = new URL(json.url);
      const path = url.pathname.replace(/\/+$/, "");
      if (!PORTAL_HOSTS.has(url.hostname) || !path.endsWith("/entra")) {
        throw new Error("Indirizzo di accesso non valido");
      }
      if (popup) {
        popup.location.href = url.toString();
      } else if (!window.open(url.toString(), "_blank")) {
        throw new Error("Il browser ha bloccato la finestra. Consenti i popup per questo sito e riprova.");
      }
    } catch (error) {
      if (popup) popup.close();
      toast.error(error.message || "Accesso al portale non riuscito");
    } finally {
      setBusyId("");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-rose-600" />
            Registrazioni portale
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Account creati su casadiriposoperte.it. Attivando una registrazione partono i 14 giorni di prova,
            la scheda entra nel catalogo e la struttura riceve l&apos;email di attivazione.
            Alla scadenza esce dal catalogo finché non paga l&apos;abbonamento.
            Entra apre il sito già dentro quell&apos;account: il link vale poco più di un minuto e si usa una volta sola.
          </p>
        </div>
        <Button variant="outline" onClick={() => load(stato)} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((filter) => (
          <Button
            key={filter.id}
            variant={stato === filter.id ? "default" : "outline"}
            onClick={() => setStato(filter.id)}
          >
            {filter.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center gap-2 p-6 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Caricamento
            </div>
          ) : items.length === 0 ? (
            <p className="p-6 text-sm text-slate-500">Nessuna registrazione in questo elenco.</p>
          ) : (
            <div className="divide-y">
              {items.map((item) => {
                const stats = schedaStats(item.scheda);
                return (
                  <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">{item.nome_struttura}</p>
                      <p className="text-sm text-slate-500">
                        {item.email}
                        {item.tipo_struttura ? ` · ${item.tipo_struttura}` : ""}
                        {item.comune ? ` · ${item.comune}${item.provincia ? ` (${item.provincia})` : ""}` : ""}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {formatWhen(item.created_at)}
                        {item.registration_source ? ` · ${item.registration_source}` : ""}
                        {` · ${stats.filled}/${stats.total} campi compilati`}
                      </p>
                      {trialText(item) ? (
                        <p className="text-xs font-medium text-sky-800 mt-1">{trialText(item)}</p>
                      ) : null}
                      {(item.pagamenti || []).length > 0 ? (
                        <p className="text-xs text-slate-500 mt-1">
                          Tentativi: {item.pagamenti.map((payment) => payment.stato_label).join(" · ")}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{statusLabel(item)}</Badge>
                      <Badge variant="outline" className={paymentBadgeClass(item.pagamento_stato)}>
                        {paymentSummary(item)}
                      </Badge>
                      <Button size="sm" variant="outline" onClick={() => setSelected(item)}>
                        <Eye className="w-4 h-4 mr-1.5" />
                        Dettagli
                      </Button>
                      <Button size="sm" variant="outline" disabled={busyId === item.id} onClick={() => enterPortal(item)}>
                        <LogIn className="w-4 h-4 mr-1.5" />
                        Entra
                      </Button>
                      {item.approval_status === "pending" ? (
                        <>
                          <Button size="sm" disabled={busyId === item.id} onClick={() => act(item.id, "attiva")}>
                            Attiva prova
                          </Button>
                          <Button size="sm" variant="outline" disabled={busyId === item.id} onClick={() => act(item.id, "rifiuta")}>
                            Rifiuta
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <SchedaDialog
        item={selected}
        busy={busyId === selected?.id}
        onEnter={() => selected && enterPortal(selected)}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function SchedaDialog({ item, busy, onEnter, onClose }) {
  const scheda = item?.scheda || {};
  const stats = schedaStats(scheda);
  return (
    <Dialog open={!!item} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item?.nome_struttura || "Scheda struttura"}</DialogTitle>
          <p className="text-sm text-slate-500">
            {stats.filled} di {stats.total} campi compilati
            {item?.email ? ` · ${item.email}` : ""}
          </p>
        </DialogHeader>
        <div className="space-y-5">
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Prova e abbonamento</h3>
            <p className="text-sm text-slate-900">{trialDialogText(item)}</p>
            <p className="mt-1 text-sm text-slate-900">
              Pagamento abbonamento: {item?.pagamento_stato_label || "Nessun pagamento"}
              {item?.subscription_plan_label ? ` · ${item.subscription_plan_label}` : ""}
              {item?.subscription_end && item?.pagamento_effettuato ? ` · fino al ${formatWhen(item.subscription_end)}` : ""}
            </p>
            {Array.isArray(item?.pagamenti) && item.pagamenti.length > 0 ? (
              <ul className="mt-2 space-y-1">
                {item.pagamenti.map((payment, index) => (
                  <li key={`${payment.created_at}-${payment.stato}-${index}`} className="text-sm text-slate-700">
                    {formatWhen(payment.created_at)}
                    {payment.piano_label ? ` · ${payment.piano_label}` : ""}
                    {payment.importo != null && payment.importo !== "" ? ` · ${euroFormat.format(Number(payment.importo))}` : ""}
                    {` · ${payment.stato_label}`}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-sm italic text-slate-400">Nessun tentativo di pagamento dell&apos;abbonamento</p>
            )}
          </section>
          {SCHEDA_GROUPS.map((group) => (
            <section key={group.title}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{group.title}</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {group.fields.map(([key, label, kind]) => {
                  const filled = fieldFilled(scheda[key], kind);
                  return (
                    <div key={key} className={key === "note_attivita" ? "sm:col-span-2" : ""}>
                      <p className="text-xs font-medium text-slate-500">{label}</p>
                      <p className={`mt-0.5 text-sm ${filled ? "text-slate-900" : "italic text-slate-400"}`}>
                        {formatField(scheda[key], kind)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Immagini</h3>
            {Array.isArray(scheda.foto) && scheda.foto.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {scheda.foto.map((photo) => (
                  <a
                    key={photo.url}
                    href={photo.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
                  >
                    <img
                      src={photo.url}
                      alt={photo.didascalia || "Foto della struttura"}
                      className="h-32 w-full object-cover"
                    />
                    {photo.didascalia ? (
                      <p className="truncate px-2 py-1 text-xs text-slate-500">{photo.didascalia}</p>
                    ) : null}
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm italic text-slate-400">Nessuna immagine</p>
            )}
          </section>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onEnter}>
            <LogIn className="w-4 h-4 mr-1.5" />
            Entra nel portale
          </Button>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
